import { Component } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";
import { Router, RouterLink } from "@angular/router";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ProductionRequestService } from "../../services/production-request.service";
import { StationService } from "../../services/station.service";
import { PermissionService } from "../../services/permission.service";
import { PageHeaderComponent } from "../../shared/page-header/page-header.component";
import { CoverageCitySelectComponent } from "../../shared/coverage-city-select/coverage-city-select.component";
import { StationSelectFieldComponent } from "../../shared/station-select-field/station-select-field.component";
import { Station } from "../../models/station.model";
import { UsCity } from "../../models/city.model";
import { SnackbarService } from "../../services/snackbar.service";

@Component({
  selector: "app-production-request-create",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PageHeaderComponent,
    CoverageCitySelectComponent,
    StationSelectFieldComponent,
  ],
  templateUrl: "./production-request-create.component.html",
  styleUrls: ["./production-request-create.component.css"],
})
export class ProductionRequestCreateComponent {
  stationId: number | null = null;
  whatBusinessDoes = "";
  campaignGoal = "";
  uniqueSellingPoint = "";
  callToAction = "";
  duration: number | null = null;
  stations: Station[] = [];
  selectedCoverageCities: UsCity[] = [];
  loadingStations = false;
  stationsLoadError = "";
  submitting = false;
  error = "";
  successMessage = "";

  durationOptions = [
    { value: null, label: "No preference" },
    { value: 15, label: "15 seconds" },
    { value: 30, label: "30 seconds" },
    { value: 60, label: "60 seconds" },
  ];

  constructor(
    private productionRequestService: ProductionRequestService,
    private stationService: StationService,
    private permissionService: PermissionService,
    private router: Router,
    private snackbar: SnackbarService
  ) {
    if (!this.permissionService.isAdvertiserUserOnly()) {
      this.router.navigate(["/production-requests"]);
      return;
    }
  }

  onCoverageCitiesChange(cities: UsCity[]): void {
    this.selectedCoverageCities = cities;
    this.stationId = null;
    this.stations = [];
    this.stationsLoadError = "";

    if (cities.length === 0) {
      return;
    }

    this.loadStationsByCities(cities.map((city) => city.name));
  }

  loadStationsByCities(cityNames: string[]): void {
    if (!cityNames.length) {
      this.stations = [];
      return;
    }

    this.loadingStations = true;
    this.stationsLoadError = "";
    this.stationService.getAllStationsByCities(cityNames).subscribe({
      next: (response) => {
        this.stations = (response.stations || []).sort((a, b) =>
          a.call_sign.localeCompare(b.call_sign),
        );
        this.loadingStations = false;
      },
      error: () => {
        this.loadingStations = false;
        this.stationsLoadError = "Failed to load stations for the selected cities.";
        this.snackbar.error("Failed to load stations for selected cities.");
      },
    });
  }

  submit(): void {
    this.error = "";
    this.successMessage = "";
    if (!this.selectedCoverageCities.length) {
      this.error = "Please select at least one coverage city.";
      return;
    }
    if (!this.stationId) {
      this.error = "Please select a station.";
      return;
    }
    if (!this.whatBusinessDoes.trim()) {
      this.error = "Please describe what your business does.";
      return;
    }
    if (!this.campaignGoal.trim()) {
      this.error = "Please enter the goal of the campaign.";
      return;
    }
    if (!this.uniqueSellingPoint.trim()) {
      this.error = "Please enter what you do that competitors can't.";
      return;
    }

    this.submitting = true;
    const payload: any = {
      station_id: this.stationId,
      what_business_does: this.whatBusinessDoes.trim(),
      campaign_goal: this.campaignGoal.trim(),
      unique_selling_point: this.uniqueSellingPoint.trim(),
    };
    const callToAction = this.callToAction.trim();
    if (callToAction) {
      payload.call_to_action = callToAction;
    }
    if (this.duration != null) {
      payload.duration = this.duration;
    }

    this.productionRequestService.create(payload).subscribe({
      next: (res) => {
        this.submitting = false;
        this.successMessage =
          "Your commercial request has been submitted successfully. A confirmation email has been sent to your email address. Our team will review your request shortly.";
        this.snackbar.success(
          "Your commercial request has been submitted successfully. A confirmation email has been sent to your email address. Our team will review your request shortly.",
        );
        setTimeout(() => this.router.navigate(["/production-requests", res.id]), 1200);
      },
      error: (err: unknown) => {
        this.submitting = false;
        const msg = this.createRequestErrorMessage(err);
        this.error = msg;
        this.snackbar.error(msg);
      },
    });
  }

  private createRequestErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const status = err.status;
      if (status === 502 || status === 503 || status === 504) {
        return "The service is temporarily unavailable. Please try again in a few minutes.";
      }
      if (status === 0) {
        return "Could not reach the server. Check your connection and try again.";
      }
      const body = err.error as { error?: string; message?: string } | string | null;
      if (body && typeof body === "object") {
        const api = body.error || body.message;
        if (api && typeof api === "string") return api;
      }
    }
    const anyErr = err as { error?: { error?: string } };
    const nested = anyErr?.error?.error;
    if (typeof nested === "string" && nested.trim()) return nested;
    return "Something went wrong. Please try again.";
  }
}
